import type React from "react"
import { type ReactNode, createContext, useContext, useState } from "react"

interface DatabaseInfo {
  databaseId: string
  customerName: string
  userId: string
}

interface TenantContextType {
  databaseId: string | null
  setDatabaseId: (id: string) => void
  clearDatabaseId: () => void
  availableDatabases: DatabaseInfo[]
  setAvailableDatabases: (databases: DatabaseInfo[]) => void
  clearAvailableDatabases: () => void
}

const TenantContext = createContext<TenantContextType | null>(null)

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider")
  }
  return context
}

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [databaseId, setDatabaseIdState] = useState<string | null>(() => {
    // Initialize from localStorage to persist across window close/reopen
    return localStorage.getItem("databaseId")
  })

  const [availableDatabases, setAvailableDatabasesState] = useState<
    DatabaseInfo[]
  >(() => {
    // Initialize from localStorage to persist across window close/reopen
    const stored = localStorage.getItem("availableDatabases")
    return stored ? JSON.parse(stored) : []
  })

  const setDatabaseId = (id: string) => {
    localStorage.setItem("databaseId", id)
    setDatabaseIdState(id)
  }

  const clearDatabaseId = () => {
    localStorage.removeItem("databaseId")
    setDatabaseIdState(null)
  }

  const setAvailableDatabases = (databases: DatabaseInfo[]) => {
    localStorage.setItem("availableDatabases", JSON.stringify(databases))
    setAvailableDatabasesState(databases)
  }

  const clearAvailableDatabases = () => {
    localStorage.removeItem("availableDatabases")
    setAvailableDatabasesState([])
  }

  const value = {
    databaseId,
    setDatabaseId,
    clearDatabaseId,
    availableDatabases,
    setAvailableDatabases,
    clearAvailableDatabases,
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}
